/**
 * ATL-1098 — the two shapes `showOnTop` legitimately has, and the only sanctioned way to read or
 * write one.
 *
 * Firestore's `stores/<id>.showOnTop` is EITHER a boolean OR a `{ admin, cashier }` map. Measured
 * fleet-wide 2026-09-01 over all 5,814 store documents: 3,149 hold `true`, 38 hold `false`, and
 * 2,627 (across 2,497 organizations) hold the map. The map's halves genuinely disagree in 135 of
 * them, so it is two settings and not a wrapper around one.
 *
 * Neither shape is going away. New stores are seeded with the scalar (`firebase.ts`) and nothing
 * writes a new map, but the 2,627 existing maps are real settings their owners chose. "Normalising"
 * in either direction is data loss wearing a tidy-up costume: collapsing a map discards the
 * `cashier` half, and expanding a scalar invents a `cashier` value the org never set.
 *
 *     READ one half. WRITE BACK the shape you read.
 *
 * WHY THIS FILE IS `.ts` AND LIVES HERE. Both call sites are `.js` screens, and this repo's lint
 * runs `--ext .ts,.tsx` — a helper left inline in a screen is checked by nothing. Extracting it
 * here puts the branch that decides whether a store's settings survive a Save under the
 * typechecker and the linter, and gives it a unit test that a rendered-component test could not.
 *
 * The backend has a deliberate twin at
 * `apps/atlas-backend/src/common/utils/show-on-top.ts` — same names, same behaviour table, same
 * tests. There is no workspace package both apps import from (the frontend and backend share only
 * the two GENERATED packages), and inventing one for forty lines is a bigger change than the bug.
 * If you change the semantics here, change them there.
 */

/** The legacy per-role shape. Keys are optional: an absent half reads as "not shown". */
export type ShowOnTopMap = {
  admin?: boolean | null
  cashier?: boolean | null
}

/** Everything `showOnTop` is allowed to be. `null`/`undefined` mean the field is absent. */
export type ShowOnTop = boolean | ShowOnTopMap

/** The roles the map distinguishes. Only `admin` is consulted by any current reader. */
export type ShowOnTopRole = 'admin' | 'cashier'

function isBooleanish (value: unknown): value is boolean | null | undefined {
  return typeof value === 'boolean' || value === null || value === undefined
}

/** True for the `{ admin, cashier }` shape — an object whose known halves are booleans. */
export function isShowOnTopMap (value: unknown): value is ShowOnTopMap {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false
  const map = value as Record<string, unknown>
  return isBooleanish(map.admin) && isBooleanish(map.cashier)
}

/**
 * Return `value` UNCHANGED if it is one of the two legal shapes, `null` if it is absent, and
 * `undefined` for anything else (a string, a number, an array, an object whose halves are not
 * booleans). `undefined` is deliberately distinct from `null`: `null` says "absent, that is fine",
 * `undefined` says "this is not a showOnTop". Nothing here coerces — that is the point.
 */
export function parseShowOnTop (value: unknown): ShowOnTop | null | undefined {
  if (value === null || value === undefined) return null
  if (typeof value === 'boolean') return value
  if (isShowOnTopMap(value)) return value
  return undefined
}

/**
 * Read one role's half.
 *
 * A scalar answers for every role — that is what a store with no per-role split means. A map
 * answers with its half, and an absent half is `false` (the field means "pinned to the top", and a
 * setting never made is not a pin). An unparseable value is `false` rather than a throw: this is a
 * display flag and a render must not crash on one bad document.
 */
export function showsOnTopFor (value: unknown, role: ShowOnTopRole): boolean {
  const parsed = parseShowOnTop(value)
  if (parsed === null || parsed === undefined) return false
  if (typeof parsed === 'boolean') return parsed
  return parsed[role] === true
}

/**
 * Set one role's half WITHOUT changing the shape.
 *
 * Given a map, returns a map with the other half untouched. Given a scalar (or an absent value),
 * returns a scalar. This is the function that stops `StoreDetailScreen`'s Save from flattening a
 * map into the `.admin` half it happened to display — it is not a convenience wrapper, it is the
 * fix.
 *
 * An unparseable previous value is treated as absent, so the result is the scalar `next`: there is
 * no half worth preserving in a value that is not a showOnTop.
 */
export function withShowOnTopFor (previous: unknown, role: ShowOnTopRole, next: boolean): ShowOnTop {
  const parsed = parseShowOnTop(previous)
  if (parsed !== null && parsed !== undefined && typeof parsed !== 'boolean') {
    return { ...parsed, [role]: next }
  }
  return next
}
